import { useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { Plus, RefreshCw, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { Logo } from '@/shared/components/Logo'
import { cn } from '@/shared/utils'
import { useInventory } from '../hooks/useInventory'
import { useVendor } from '../hooks/useVendor'
import { useCategories } from '../hooks/useCategories'
import { InventoryTable } from '../components/InventoryTable'
import { VariantDrawer } from '../components/VariantDrawer'
import { AddEditItemDialog } from '../components/AddEditItemDialog'
import type { InventoryItem, ClothingItem } from '@/shared/types'

export function InventoryPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation()
  const { vendor } = useVendor(vendorId || '')
  const { inventory, isProcessing, refetch, handleAddEdit, toggleAvailability, deleteFood, deleteClothing } =
    useInventory(vendorId || '')
  const { categories } = useCategories(vendorId || '')

  const [selectedClothingItem, setSelectedClothingItem] = useState<ClothingItem | null>(null)
  const [isVariantDrawerOpen, setIsVariantDrawerOpen] = useState(false)
  const [deleteItem, setDeleteItem] = useState<InventoryItem | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // AddEditItemDialog state
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false)
  const [editItem, setEditItem] = useState<InventoryItem | null>(null)
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add')

  const USE_FOOD_VENDOR = vendor?.type === 'FOOD'

  const refreshInventory = async () => {
    setIsRefreshing(true)
    await refetch()
    setIsRefreshing(false)
  }

  const handleDelete = () => {
    if (!deleteItem || !vendor) return

    if (USE_FOOD_VENDOR) {
      deleteFood(deleteItem.id)
    } else {
      deleteClothing(deleteItem.id)
    }
    setDeleteItem(null)
  }

  const handleViewVariants = (item: ClothingItem) => {
    setSelectedClothingItem(item)
    setIsVariantDrawerOpen(true)
  }

  const handleOpenAddDialog = () => {
    setEditItem(null)
    setDialogMode('add')
    setIsAddEditDialogOpen(true)
  }

  const handleOpenEditDialog = (item: InventoryItem) => {
    setEditItem(item)
    setDialogMode('edit')
    setIsAddEditDialogOpen(true)
  }

  const handleSaveItem = async (item: InventoryItem, imageFile?: File, imageFiles?: File[]) => {
    if (!vendor) return

    await handleAddEdit(item, vendor.type, editItem, imageFile, imageFiles)
    setIsAddEditDialogOpen(false)
    setEditItem(null)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card/90 border-b sticky top-0 z-20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Logo href={`/dashboard/${vendorId}`} />
              <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                <Link
                  to={`/dashboard/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/dashboard/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/inventory/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/inventory/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Inventory
                </Link>
                <Link
                  to={`/orders/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/orders/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Orders
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={refreshInventory} disabled={isRefreshing || isProcessing}>
                <RefreshCw className={cn('w-4 h-4 mr-2', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
              <Button size="sm" disabled={isProcessing} onClick={handleOpenAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-foreground">Inventory</h1>
          <p className="text-sm text-muted-foreground">
            Manage your {USE_FOOD_VENDOR ? 'food items' : 'clothing products'}
          </p>
        </div>
        <InventoryTable
          items={inventory}
          vendorType={vendor?.type || 'FOOD'}
          onEdit={handleOpenEditDialog}
          onDelete={(item) => setDeleteItem(item)}
          onToggleAvailability={(item) => toggleAvailability(item)}
          onViewVariants={handleViewVariants}
          loading={isProcessing}
        />
      </div>

      <VariantDrawer item={selectedClothingItem} open={isVariantDrawerOpen} onOpenChange={setIsVariantDrawerOpen} />

      {/* Add/Edit Item Dialog */}
      <AddEditItemDialog
        open={isAddEditDialogOpen}
        onOpenChange={setIsAddEditDialogOpen}
        vendorType={vendor?.type || 'FOOD'}
        mode={dialogMode}
        item={editItem}
        onSave={handleSaveItem}
        loading={isProcessing}
        categories={categories}
      />

      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the item "{deleteItem?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
